import React, { useState } from 'react';
import { Schedule, DayOfWeek, ScheduleKind, SCHEDULE_KIND_LABELS, localIsoDate, scheduleForPreset, scheduleKind } from '../domain';
import { parseIntervalString, formatIntervalString } from '../lib/dateUtils';
import { Input, NumberInput, Select, cx, focusRing } from './ui';

/** A toggle in one of the schedule's rows of options (kind, weekday, monthly mode). */
const optionClass = (selected: boolean) => cx(
  'h-8 rounded-lg px-3 text-sm font-medium transition-colors',
  focusRing,
  selected
    ? 'bg-indigo-600 text-white shadow-sm dark:shadow-none'
    : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
);

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ORDINALS: { value: 1 | 2 | 3 | 4 | -1; label: string }[] = [
  { value: 1, label: 'first' },
  { value: 2, label: 'second' },
  { value: 3, label: 'third' },
  { value: 4, label: 'fourth' },
  { value: -1, label: 'last' },
];


interface ScheduleInputProps {
  value: Schedule;
  onChange: (schedule: Schedule) => void;
}

const ScheduleInput: React.FC<ScheduleInputProps> = ({ value, onChange }) => {
  const [minuteString, setMinuteString] = useState(
    value.type === 'minutes' ? formatIntervalString(value.interval) : '1d'
  );

  const handleTypeChange = (type: ScheduleKind) => {
    switch (type) {
      case 'minutes':
        onChange({ type: 'minutes', interval: 1440 });
        setMinuteString('1d');
        break;
      case 'weekly':
        onChange({ type: 'weekly', days: ['monday'] });
        break;
      case 'monthly':
        onChange({ type: 'monthly', dayOfMonth: 1 });
        break;
      case 'yearly':
        onChange({ type: 'yearly', month: 1, dayOfMonth: 1 });
        break;
      case 'once':
        onChange(scheduleForPreset('once', 'fixed'));
        break;
    }
  };

  const parsedMinutes = parseIntervalString(minuteString);
  const today = localIsoDate(new Date());

  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap">
        {/* 'monthlyWeekday' isn't its own top-level button — it's a mode within the "Monthly"
            panel, toggled alongside 'monthly' below. */}
        {(Object.keys(SCHEDULE_KIND_LABELS) as ScheduleKind[]).map(type => (
          <button
            key={type}
            type="button"
            onClick={() => handleTypeChange(type)}
            aria-pressed={scheduleKind(value) === type}
            className={optionClass(scheduleKind(value) === type)}
          >
            {SCHEDULE_KIND_LABELS[type]}
          </button>
        ))}
      </div>

      {value.type === 'minutes' && (
        <div>
          <Input
            type="text"
            aria-label="Interval"
            value={minuteString}
            onChange={(e) => {
              setMinuteString(e.target.value);
              const parsed = parseIntervalString(e.target.value);
              if (parsed) onChange({ type: 'minutes', interval: parsed });
            }}
            invalid={!parsedMinutes}
            placeholder="e.g. 3d, 2w, 1y"
          />
          {parsedMinutes ? (
            <p className="text-emerald-700 dark:text-emerald-400 text-xs mt-1">= {formatIntervalString(parsedMinutes)} after it's done</p>
          ) : (
            <p className="text-red-600 dark:text-red-400 text-xs mt-1">Invalid format. Use: 1y 2w 3d (a day is the shortest interval)</p>
          )}
        </div>
      )}

      {value.type === 'weekly' && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {DAYS.map(({ value: day, label }) => {
              const selected = value.days.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const days = selected
                      ? value.days.filter(d => d !== day)
                      : [...value.days, day];
                    if (days.length > 0) onChange({ ...value, days });
                  }}
                  aria-pressed={selected}
                  className={optionClass(selected)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            Every
            <NumberInput
              compact
              min={1}
              max={52}
              value={value.interval ?? 1}
              onChange={(n) => onChange({ ...value, interval: n })}
              className="w-16"
            />
            week(s)
          </label>
        </div>
      )}

      {(value.type === 'monthly' || value.type === 'monthlyWeekday') && (
        <div className="space-y-2">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onChange({ type: 'monthly', dayOfMonth: 1, interval: value.interval })}
              aria-pressed={value.type === 'monthly'}
              className={optionClass(value.type === 'monthly')}
            >
              On a date
            </button>
            <button
              type="button"
              onClick={() => onChange({ type: 'monthlyWeekday', weekday: 'monday', ordinal: 1, interval: value.interval })}
              aria-pressed={value.type === 'monthlyWeekday'}
              className={optionClass(value.type === 'monthlyWeekday')}
            >
              On a weekday
            </button>
          </div>

          {value.type === 'monthly' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 flex-wrap">
              On day
              <NumberInput
                compact
                min={1}
                max={31}
                value={value.dayOfMonth}
                onChange={(n) => onChange({ ...value, dayOfMonth: n })}
                className="w-16"
              />
              of every
              <NumberInput
                compact
                min={1}
                max={12}
                value={value.interval ?? 1}
                onChange={(n) => onChange({ ...value, interval: n })}
                className="w-16"
              />
              month(s)
            </label>
          )}

          {value.type === 'monthlyWeekday' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 flex-wrap">
              On the
              <Select
                compact
                value={value.ordinal}
                onChange={(e) => onChange({ ...value, ordinal: parseInt(e.target.value) as 1 | 2 | 3 | 4 | -1 })}
              >
                {ORDINALS.map(({ value: v, label }) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </Select>
              <Select
                compact
                value={value.weekday}
                onChange={(e) => onChange({ ...value, weekday: e.target.value as DayOfWeek })}
              >
                {DAYS.map(({ value: day, label }) => (
                  <option key={day} value={day}>{label}</option>
                ))}
              </Select>
              of every
              <NumberInput
                compact
                min={1}
                max={12}
                value={value.interval ?? 1}
                onChange={(n) => onChange({ ...value, interval: n })}
                className="w-16"
              />
              month(s)
            </label>
          )}
        </div>
      )}

      {value.type === 'once' && (
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            Due date
            <Input
              compact
              type="date"
              value={value.dueDate}
              min={value.dueDate < today ? value.dueDate : today}
              onChange={(e) => { if (e.target.value) onChange({ type: 'once', dueDate: e.target.value }); }}
              required
            />
          </label>
        </div>
      )}

      {value.type === 'yearly' && (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 flex-wrap">
          On
          <Select
                compact
            value={value.month}
            onChange={(e) => onChange({ ...value, month: parseInt(e.target.value) })}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </Select>
          <NumberInput
            compact
            min={1}
            max={31}
            value={value.dayOfMonth}
            onChange={(n) => onChange({ ...value, dayOfMonth: n })}
            className="w-16"
          />
        </label>
      )}
    </div>
  );
};

export default ScheduleInput;
